import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { ACTIONS, ACTION_LABEL, MODULE_GROUPS, PAGE_ITEMS, emptyModulePerms } from "@/lib/permissionsSchema";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FieldsPicker } from "@/components/permissions/FieldsPicker";
import { Save } from "lucide-react";
import { toast } from "sonner";

export function UserOverrideDialog({ userId, userLabel, open, onOpenChange }) {
  const [doc, setDoc] = useState(null); // { role, role_defaults, overrides }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    api.get(`/permissions/users/${userId}`)
      .then(({ data }) => setDoc(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, userId]);

  if (!open) return null;

  const isSuperAdmin = doc?.role === "SuperAdmin";
  const overrideModules = doc?.overrides?.modules || {};
  const overridePages = doc?.overrides?.pages || {};
  const roleDefaults = doc?.role_defaults || { modules: {}, pages: {} };

  const setModuleOverride = (collection, perms) => {
    setDoc((prev) => ({ ...prev, overrides: { ...prev.overrides, modules: { ...prev.overrides.modules, [collection]: perms } } }));
  };
  const clearModuleOverride = (collection) => {
    setDoc((prev) => {
      const modules = { ...prev.overrides.modules };
      delete modules[collection];
      return { ...prev, overrides: { ...prev.overrides, modules } };
    });
  };
  const setPageOverride = (page, view) => {
    setDoc((prev) => ({ ...prev, overrides: { ...prev.overrides, pages: { ...prev.overrides.pages, [page]: { view } } } }));
  };
  const clearPageOverride = (page) => {
    setDoc((prev) => {
      const pages = { ...prev.overrides.pages };
      delete pages[page];
      return { ...prev, overrides: { ...prev.overrides, pages } };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/permissions/users/${userId}`, { modules: doc.overrides.modules, pages: doc.overrides.pages });
      toast.success("User overrides saved");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="font-display">Permission overrides — {userLabel}</DialogTitle>
          <DialogDescription>
            Base role: <span className="font-medium text-foreground">{doc?.role || "…"}</span>.
            Turn on "Override" for a page or module to set values just for this person; leave it off to keep inheriting from their role.
          </DialogDescription>
        </DialogHeader>

        {loading || !doc ? <Skeleton className="h-96 rounded-xl" /> : (
          <>
            {isSuperAdmin && (
              <Badge variant="outline" className="text-[10px] border-primary/40 text-primary w-fit">
                SuperAdmin already has full access — overrides have no effect
              </Badge>
            )}
            <ScrollArea className="max-h-[60vh] pr-3">
              <div className="space-y-4">
                <div className="rounded-xl border border-border overflow-hidden">
                  <div className="px-4 py-2.5 border-b border-border bg-muted/40"><p className="text-sm font-semibold">Pages</p></div>
                  <div className="divide-y divide-border">
                    {PAGE_ITEMS.map((p) => {
                      const overridden = p.key in overridePages;
                      const effective = overridden ? overridePages[p.key].view : !!roleDefaults.pages?.[p.key]?.view;
                      return (
                        <div key={p.key} className="flex items-center justify-between px-4 py-2 text-sm gap-4">
                          <span className="flex-1">{p.label}</span>
                          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Checkbox
                              checked={overridden}
                              disabled={isSuperAdmin}
                              onCheckedChange={(v) => v ? setPageOverride(p.key, effective) : clearPageOverride(p.key)}
                            /> Override
                          </label>
                          <Switch
                            checked={effective}
                            disabled={!overridden || isSuperAdmin}
                            onCheckedChange={(v) => setPageOverride(p.key, v)}
                            data-testid={`user-page-${p.key}-view`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {MODULE_GROUPS.map((group) => (
                  <div key={group.label} className="rounded-xl border border-border overflow-hidden">
                    <div className="px-4 py-2.5 border-b border-border bg-muted/40"><p className="text-sm font-semibold">{group.label}</p></div>
                    <div className="grid grid-cols-[auto_1fr_repeat(4,60px)_auto] items-center gap-2 py-1.5 px-3 border-b border-border text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
                      <span>Override</span><span>Module</span>
                      {ACTIONS.map((a) => <span key={a} className="text-center">{ACTION_LABEL[a]}</span>)}
                      <span className="text-right pr-1">Hidden</span>
                    </div>
                    {group.items.map((item) => {
                      const collection = item.key;
                      const overridden = collection in overrideModules;
                      const effective = overridden
                        ? { ...emptyModulePerms(), ...overrideModules[collection] }
                        : (roleDefaults.modules?.[collection] || emptyModulePerms());
                      return (
                        <div key={collection} className="grid grid-cols-[auto_1fr_repeat(4,60px)_auto] items-center gap-2 py-1.5 px-3 border-b border-border last:border-b-0 text-sm">
                          <Checkbox
                            checked={overridden}
                            disabled={isSuperAdmin}
                            onCheckedChange={(v) => v ? setModuleOverride(collection, effective) : clearModuleOverride(collection)}
                          />
                          <span className="truncate">{item.label}</span>
                          {ACTIONS.map((a) => (
                            <div key={a} className="flex justify-center">
                              <Checkbox
                                checked={!!effective[a]}
                                disabled={!overridden || isSuperAdmin}
                                onCheckedChange={(v) => setModuleOverride(collection, { ...effective, [a]: !!v })}
                                data-testid={`user-perm-${collection}-${a}`}
                              />
                            </div>
                          ))}
                          <FieldsPicker
                            collection={collection}
                            value={effective.hidden_fields}
                            disabled={!overridden || isSuperAdmin}
                            onChange={(hf) => setModuleOverride(collection, { ...effective, hidden_fields: hf })}
                          />
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || loading || isSuperAdmin} className="kn-gradient-bg text-white border-0" data-testid="save-user-overrides">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save overrides"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
